// External libraries
import { useState } from 'react';
import {
  LayoutDashboard,
  Receipt,
  BarChart3,
  DollarSign,
  TrendingUp,
  Users,
  Settings,
  Building2,
  Database,
  LogOut,
  ChevronLeft,
  ChevronRight,
  FileCheck2,
} from 'lucide-react';
import type { User } from '@supabase/supabase-js';

// Internal - Components
import { cn } from '../ui/utils';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { CompanySwitcher } from './CompanySwitcher';

// Internal - Utils
import { getInitials } from '../../lib/utils/format';

// Internal - Hooks
import { usePermissions } from '../../hooks/usePermissions';

interface DesktopSidebarProps {
  /**
   * Current authenticated user
   */
  user: User | null;
  /**
   * Active route path
   */
  currentPath: string;
  /**
   * Navigation handler
   */
  onNavigate: (path: string) => void;
  /**
   * Sign out handler
   */
  onSignOut: () => void;
}

/**
 * DesktopSidebar - Collapsible navigation sidebar for desktop
 * 
 * Features:
 * - Collapsible to icon-only mode
 * - Company switcher integration
 * - User profile section
 * - Tooltips for collapsed state
 * - Grouped navigation items
 * 
 * @example
 * ```tsx
 * <DesktopSidebar
 *   user={currentUser}
 *   currentPath="/dashboard"
 *   onNavigate={handleNavigate}
 *   onSignOut={handleSignOut}
 * />
 * ```
 */
export function DesktopSidebar({
  user,
  currentPath,
  onNavigate,
  onSignOut,
}: DesktopSidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { canAccess, canEdit } = usePermissions();

  // Define all navigation items
  const allNavigation = [
    {
      title: 'Principal',
      items: [
        { title: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
        { title: 'Lançamentos', icon: Receipt, path: '/entries' },
        { title: 'Relatórios', icon: BarChart3, path: '/reports' },
      ],
    },
    {
      title: 'Planejamento',
      items: [
        { title: 'Orçamento', icon: DollarSign, path: '/budget' },
        { title: 'Cenários', icon: TrendingUp, path: '/scenarios' },
      ],
    },
    {
      title: 'Gestão',
      items: [
        { title: 'Compartilhamento', icon: Users, path: '/sharing', requiresAccess: 'sharing' as const },
        { title: 'Conciliação Bancária', icon: FileCheck2, path: '/reconciliation' },
        { title: 'Dados Mestres', icon: Database, path: '/configurations', requiresAccess: 'configurations' as const },
        { title: 'Configurações', icon: Settings, path: '/settings', requiresAccess: 'settings' as const },
      ],
    },
  ];

  // Filter navigation items based on permissions
  const navigation = allNavigation.map(section => ({
    ...section,
    items: section.items.filter(item => {
      // Special case: Reconciliation requires edit permission for transactions
      if (item.path === '/reconciliation') {
        return canEdit('transactions');
      }
      
      // If item requires access permission, check it
      if (item.requiresAccess) {
        return canAccess(item.requiresAccess);
      }
      // Otherwise, show the item
      return true;
    })
  })).filter(section => section.items.length > 0); // Remove empty sections

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          "hidden md:flex flex-col border-r bg-background transition-all duration-300",
          isCollapsed ? "w-16" : "w-64"
        )}
      >
        {/* Header - Company Switcher */}
        <div className="border-b px-3 py-3">
          {!isCollapsed ? (
            <CompanySwitcher />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground mx-auto">
              <Building2 className="h-5 w-5" />
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-6">
          {navigation.map((section) => (
            <div key={section.title}>
              {!isCollapsed && (
                <h4 className="mb-2 px-2 text-muted-foreground">
                  {section.title}
                </h4>
              )}
              <div className="space-y-1">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = currentPath === item.path;

                  const button = (
                    <button
                      key={item.path}
                      onClick={() => onNavigate(item.path)}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg px-3 py-2 transition-colors",
                        "hover:bg-accent hover:text-accent-foreground",
                        isActive && "bg-accent text-accent-foreground font-medium",
                        isCollapsed && "justify-center"
                      )}
                      aria-label={item.title}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <Icon className="h-5 w-5 shrink-0" />
                      {!isCollapsed && <span className="truncate">{item.title}</span>}
                    </button>
                  );

                  if (isCollapsed) {
                    return (
                      <Tooltip key={item.path}>
                        <TooltipTrigger asChild>
                          {button}
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          {item.title}
                        </TooltipContent>
                      </Tooltip>
                    );
                  }

                  return button;
                })}
              </div>
            </div>
          ))}
        </nav>

        <Separator />

        {/* Footer */}
        <div className="p-4">
          {!isCollapsed ? (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {user?.email ? getInitials(user.email) : 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="truncate">
                    {user?.email || 'Usuário'}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={onSignOut}
                className="shrink-0"
                aria-label="Sair"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onSignOut}
                  className="w-full"
                  aria-label="Sair"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                Sair
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        <Separator />

        {/* Toggle Button */}
        <div className="p-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="w-full justify-center"
            aria-label={isCollapsed ? 'Expandir sidebar' : 'Recolher sidebar'}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <>
                <ChevronLeft className="h-4 w-4" />
                <span className="ml-2">Recolher</span>
              </>
            )}
          </Button>
        </div>
      </aside>
    </TooltipProvider>
  );
}
